/**
 * Authentication utility functions for role verification and localStorage management
 * This prevents role mismatch issues when switching between tabs
 * Updated with enhanced protection against role corruption
 */

import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config';

/**
 * Validates that a role value is legitimate
 * @param {string} role - The role to validate
 * @returns {boolean} - True if role is valid
 */
const isValidRole = (role) => {
  const validRoles = [
    'student', 'agent', 'admin', 
    'sub_admin_usa', 'sub_admin_aus_nz', 'sub_admin_uk', 
    'sub_admin_canada', 'sub_admin_europe'
  ];
  return validRoles.includes(role);
};

/**
 * Safely retrieves and validates role from localStorage
 * @returns {string|null} - Valid role or null if corrupted/missing
 */
const getSafeStoredRole = () => {
  try {
    const storedRole = localStorage.getItem("userRole");
    if (!storedRole) return null;
    
    // Validate the role to prevent corrupted data
    if (!isValidRole(storedRole)) {
      console.warn(`[AuthUtils] Invalid role detected in localStorage: '${storedRole}'. Clearing corrupted data.`);
      localStorage.removeItem("userRole");
      return null;
    }
    
    return storedRole;
  } catch (error) {
    console.error("[AuthUtils] Error reading role from localStorage:", error);
    return null;
  }
};

/**
 * Safely stores role in localStorage with validation
 * @param {string} role - The role to store
 * @returns {boolean} - True if successfully stored
 */
const setSafeStoredRole = (role) => {
  try {
    if (!isValidRole(role)) {
      console.error(`[AuthUtils] Attempted to store invalid role: '${role}'`);
      return false;
    }
    
    localStorage.setItem("userRole", role);
    console.log(`[AuthUtils] Successfully stored role: ${role}`);
    return true;
  } catch (error) {
    console.error("[AuthUtils] Error storing role in localStorage:", error);
    return false;
  }
};

/**
 * Checks if user is authenticated and has the correct role for the current dashboard
 * @param {string} expectedRole - The role expected for current dashboard ('agent', 'student', 'admin', etc.)
 * @param {function} navigate - React Router navigate function
 * @param {function} setLoading - Loading state setter
 * @param {function} setError - Error state setter
 * @param {function} setUserData - User data setter (optional)
 * @returns {Promise<boolean>} - Returns true if user is authorized, false otherwise
 */
export const verifyUserAuthentication = async (expectedRole, navigate, setLoading, setError, setUserData = null) => {
  try {
    // Add small delay to prevent race conditions with localStorage
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Validate the expected role first
    if (!isValidRole(expectedRole)) {
      console.error(`[AuthUtils] Invalid expected role: '${expectedRole}'`);
      setError("Invalid dashboard configuration. Please contact support.");
      setLoading(false);
      return false;
    }
    
    let userEmail = localStorage.getItem("userEmail");
    let storedUserRole = getSafeStoredRole(); // Use safe getter
    
    console.log(`[AuthUtils] Verifying authentication for ${expectedRole} dashboard`);
    console.log(`[AuthUtils] Stored data - Email: ${userEmail}, Role: ${storedUserRole}`);
    
    // CRITICAL FIX: If we just navigated and there's no stored data yet, wait a bit more
    if (!userEmail || !storedUserRole) {
      console.log("[AuthUtils] Missing authentication data, checking again after short delay...");
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const retryEmail = localStorage.getItem("userEmail");
      const retryRole = getSafeStoredRole();
      
      if (!retryEmail) {
        console.log("[AuthUtils] Still no user email found after retry");
        setError("User not logged in.");
        setLoading(false);
        return false;
      }
      
      // Update variables with retry values
      userEmail = retryEmail;
      storedUserRole = retryRole;
      console.log(`[AuthUtils] Retry results - Email: ${userEmail}, Role: ${storedUserRole}`);
    }

    // Primary check: Use stored role if available (more reliable than API calls)
    if (storedUserRole && storedUserRole !== expectedRole) {
      console.log(`[AuthUtils] Stored role mismatch! User role: ${storedUserRole}, expected: ${expectedRole}`);
      
      // Show user-friendly message based on their actual role
      const dashboardNames = {
        'student': 'Student Dashboard',
        'agent': 'Agent Dashboard', 
        'admin': 'Admin Dashboard',
        'sub_admin_usa': 'Sub-Admin USA Dashboard',
        'sub_admin_aus_nz': 'Sub-Admin AUS/NZ Dashboard',
        'sub_admin_uk': 'Sub-Admin UK Dashboard',
        'sub_admin_canada': 'Sub-Admin Canada Dashboard',
        'sub_admin_europe': 'Sub-Admin Europe Dashboard'
      };
      
      const currentDashboardName = dashboardNames[expectedRole] || `${expectedRole.charAt(0).toUpperCase() + expectedRole.slice(1)} Dashboard`;
      const correctDashboardName = dashboardNames[storedUserRole] || `${storedUserRole.charAt(0).toUpperCase() + storedUserRole.slice(1)} Dashboard`;
      
      toast.error(
        `Access denied! You are logged in as ${storedUserRole}. This is the ${currentDashboardName}. Please use the ${correctDashboardName}.`, 
        { autoClose: 4000 }
      );
      
      // Redirect to correct dashboard instead of clearing localStorage
      setTimeout(() => {
        redirectToCorrectDashboard(storedUserRole, navigate);
      }, 2000);
      
      return false;
    }
    
    // If stored role matches expected role, proceed with API verification (if needed)
    if (storedUserRole === expectedRole) {
      console.log(`[AuthUtils] Stored role matches expected (${expectedRole}), proceeding...`);

      // Render immediately using locally stored user data
      const cachedUserRaw = localStorage.getItem("userData");
      let cachedUser = null;
      try {
        cachedUser = cachedUserRaw ? JSON.parse(cachedUserRaw) : null;
      } catch (error) {
        console.warn("[AuthUtils] Failed to parse cached userData, using fallback:", error);
      }

      if (setUserData) {
        setUserData({
          email: cachedUser?.email || userEmail || "",
          role: storedUserRole,
          id: cachedUser?.id || localStorage.getItem("userId") || "",
          fullname: cachedUser?.fullname || "",
          phone_number: cachedUser?.phone_number || "",
        });
      }

      // Clear loading immediately so dashboard can paint without waiting for API
      setLoading(false);

      // Refresh user details in background (non-blocking)
      axios
        .get(`${API_BASE_URL}/api/user?email=${userEmail}`, { timeout: 6000 })
        .then((response) => {
          const userData = response.data || {};
          console.log("[AuthUtils] Background API user data:", userData);

          // Double-check API role against stored role
          if (userData.role && userData.role !== storedUserRole) {
            console.warn(`[AuthUtils] Role discrepancy! Stored: ${storedUserRole}, API: ${userData.role}`);

            // Update localStorage with latest role from API (with validation)
            if (!setSafeStoredRole(userData.role)) {
              console.error(`[AuthUtils] Failed to store API role: ${userData.role}`);
              setError("Authentication data corruption detected. Please log in again.");
              return;
            }

            // If API role is different from expected role, redirect
            if (userData.role !== expectedRole) {
              toast.error(
                `Your account role has been updated to ${userData.role}. Redirecting to the correct dashboard...`,
                { autoClose: 4000 }
              );

              setTimeout(() => {
                redirectToCorrectDashboard(userData.role, navigate);
              }, 2000);

              return;
            }
          }

          // Update user data if setter is provided
          if (setUserData) {
            setUserData(userData);
          }
        })
        .catch((apiError) => {
          console.warn("[AuthUtils] Background API call failed, using cached auth data:", apiError.message);
        });

      return true;
    }
    
    // Fallback: No stored role, must verify with API
    console.log("[AuthUtils] No stored role, verifying with API...");
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user?email=${userEmail}`);
      const userData = response.data || {};
      
      console.log("[AuthUtils] API verification result:", userData);
      
      // Check if role is missing from API
      if (!userData.role) {
        console.error("[AuthUtils] CRITICAL: Role field missing from API response");
        toast.error("Authentication error: User role information missing. Please contact support.", { autoClose: 5000 });
        setError("User role information is missing from server response.");
        setLoading(false);
        return false;
      }
      
      // Store role for future use (with validation)
      if (!setSafeStoredRole(userData.role)) {
        console.error(`[AuthUtils] Failed to store user role: ${userData.role}`);
        setError("Authentication data corruption detected. Please log in again.");
        setLoading(false);
        return false;
      }
      
      // Store user ID safely
      try {
        localStorage.setItem("userId", userData.id || "");
      } catch (error) {
        console.warn("[AuthUtils] Failed to store userId:", error);
      }
      
      // Check if user has correct role
      if (userData.role !== expectedRole) {
        console.log(`[AuthUtils] API role mismatch! User role: ${userData.role}, expected: ${expectedRole}`);
        
        const dashboardNames = {
          'student': 'Student Dashboard',
          'agent': 'Agent Dashboard', 
          'admin': 'Admin Dashboard',
          'sub_admin_usa': 'Sub-Admin USA Dashboard',
          'sub_admin_aus_nz': 'Sub-Admin AUS/NZ Dashboard',
          'sub_admin_uk': 'Sub-Admin UK Dashboard',
          'sub_admin_canada': 'Sub-Admin Canada Dashboard',
          'sub_admin_europe': 'Sub-Admin Europe Dashboard'
        };
        
        const currentDashboardName = dashboardNames[expectedRole] || `${expectedRole.charAt(0).toUpperCase() + expectedRole.slice(1)} Dashboard`;
        const correctDashboardName = dashboardNames[userData.role] || `${userData.role.charAt(0).toUpperCase() + userData.role.slice(1)} Dashboard`;
        
        toast.error(
          `Access denied! You are logged in as ${userData.role}. This is the ${currentDashboardName}. Redirecting to ${correctDashboardName}...`, 
          { autoClose: 4000 }
        );
        
        setTimeout(() => {
          redirectToCorrectDashboard(userData.role, navigate);
        }, 2000);
        
        return false;
      }
      
      // Update user data if setter is provided
      if (setUserData) {
        setUserData(userData);
      }
      
      console.log(`[AuthUtils] User authentication successful for ${expectedRole} dashboard`);
      setLoading(false);
      return true;
      
    } catch (apiError) {
      console.error("[AuthUtils] API authentication failed:", apiError.message);
      setError("Unable to verify user authentication. Please try logging in again.");
      setLoading(false);
      return false;
    }
    
  } catch (error) {
    console.error("[AuthUtils] Unexpected error during authentication:", error);
    setError("Authentication error occurred. Please try again.");
    setLoading(false);
    return false;
  }
};

/**
 * Redirects user to the correct dashboard based on their role
 * @param {string} userRole - The user's role
 * @param {function} navigate - React Router navigate function
 */
export const redirectToCorrectDashboard = (userRole, navigate) => {
  const dashboardRoutes = {
    'student': '/student-dash',
    'agent': '/agent-dashboard', 
    'admin': '/admin-dashboard',
    'sub_admin_usa': '/sub-admin-usa-dashboard',
    'sub_admin_aus_nz': '/sub-admin-aus-nz-dashboard',
    'sub_admin_uk': '/sub-admin-uk-dashboard',
    'sub_admin_canada': '/sub-admin-canada-dashboard',
    'sub_admin_europe': '/sub-admin-europe-dashboard'
  };
  
  const route = dashboardRoutes[userRole] || '/';
  console.log(`[AuthUtils] Redirecting to: ${route}`);
  navigate(route);
};

/**
 * Safely clears user authentication data with enhanced cross-session protection
 * @param {function} navigate - React Router navigate function (optional)
 */
export const clearUserAuth = (navigate = null) => {
  try {
    console.log("[AuthUtils] Starting enhanced authentication clear...");
    
    // Clear ALL localStorage to prevent cross-contamination
    const allLocalStorageKeys = Object.keys(localStorage);
    console.log(`[AuthUtils] Clearing ${allLocalStorageKeys.length} localStorage keys`);
    localStorage.clear();
    
    // Clear ALL sessionStorage
    const allSessionStorageKeys = Object.keys(sessionStorage);
    console.log(`[AuthUtils] Clearing ${allSessionStorageKeys.length} sessionStorage keys`);
    sessionStorage.clear();
    
    // Clear any authentication-related cookies
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    console.log("[AuthUtils] Enhanced authentication data cleared - ALL storage cleared");
    
    if (navigate) {
      navigate('/');
    }
  } catch (error) {
    console.error("[AuthUtils] Error clearing user auth:", error);
  }
};

/**
 * Checks if user is authenticated (has valid email and role)
 * @returns {boolean} - True if user appears to be authenticated
 */
export const isUserAuthenticated = () => {
  const userEmail = localStorage.getItem("userEmail");
  const userRole = localStorage.getItem("userRole");
  return !!(userEmail && userRole);
};

/**
 * Adds protection against cross-tab role corruption
 * This creates a unique session identifier that helps detect when data might be corrupted
 */
const initializeSessionProtection = () => {
  const sessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
  sessionStorage.setItem('sessionId', sessionId);
  localStorage.setItem('lastSessionId', sessionId);
  return sessionId;
};

/**
 * Checks if current session might have corrupted data due to cross-tab interference
 * @returns {boolean} - True if session appears valid
 */
const isSessionValid = () => {
  try {
    const currentSessionId = sessionStorage.getItem('sessionId');
    const lastStoredSessionId = localStorage.getItem('lastSessionId');
    
    if (!currentSessionId) {
      console.warn('[AuthUtils] No session ID found, initializing new session');
      initializeSessionProtection();
      return true; // New session is valid
    }
    
    // If localStorage session is different and recent (within 5 minutes), there might be cross-tab issues
    if (lastStoredSessionId && lastStoredSessionId !== currentSessionId) {
      const lastSessionTime = parseInt(lastStoredSessionId.split('')[0]) || 0;
      const timeDiff = Date.now() - lastSessionTime;
      
      if (timeDiff < 300000) { // 5 minutes
        console.warn('[AuthUtils] Potential cross-tab session detected');
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.warn('[AuthUtils] Session validation failed:', error);
    return true; // Default to valid to not break user experience
  }
};

/**
 * Enhanced localStorage protection with session validation
 * @param {string} key - The localStorage key
 * @returns {string|null} - The value or null if potentially corrupted
 */
const getProtectedStorageItem = (key) => {
  try {
    if (!isSessionValid()) {
      console.warn(`[AuthUtils] Session invalid, clearing potentially corrupted ${key}`);
      return null;
    }
    
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`[AuthUtils] Error getting protected storage item ${key}:`, error);
    return null;
  }
};

/**
 * Gets the current user's data from localStorage with session protection
 * @returns {Object} - User data object
 */
export const getCurrentUser = () => {
  return {
    email: getProtectedStorageItem("userEmail") || "",
    role: getSafeStoredRole() || "",
    id: getProtectedStorageItem("userId") || ""
  };
};

/**
 * Verifies current user authentication status
 * Used by ProtectedRoute component
 * @returns {Promise<boolean>} - True if user is authenticated and valid
 */
export const verifyAuthentication = async () => {
  try {
    console.log('[AuthUtils] Verifying authentication...');
    
    const userData = localStorage.getItem('userData');
    const token = localStorage.getItem('token');
    
    if (!userData || !token) {
      console.log('[AuthUtils] No authentication data found');
      return false;
    }
    
    // Parse user data
    let user;
    try {
      user = JSON.parse(userData);
    } catch (parseError) {
      console.error('[AuthUtils] Failed to parse user data:', parseError);
      return false;
    }
    
    // Validate user data structure
    if (!user.email || !user.role) {
      console.log('[AuthUtils] Invalid user data structure');
      return false;
    }
    
    // Validate role
    if (!isValidRole(user.role)) {
      console.log(`[AuthUtils] Invalid role: ${user.role}`);
      return false;
    }
    
    // Optional: Verify token with server (can be implemented later)
    // For now, we'll trust the local storage data if it's structurally valid
    
    console.log(`[AuthUtils] Authentication verified for user: ${user.email}, role: ${user.role}`);
    return true;
    
  } catch (error) {
    console.error('[AuthUtils] Error during authentication verification:', error);
    return false;
  }
};

/**
 * Initialize session protection when auth utils are loaded
 */
export const initializeAuthSession = () => {
  const sessionId = initializeSessionProtection();
  console.log('[AuthUtils] Session protection initialized:', sessionId);
  return sessionId;
};
